import React, { useEffect, useRef } from 'react'

import { Modal, InputNumber, Form, Select } from 'antd'
import { useTranslation } from 'react-i18next'

// 可重印標籤的 fulfillment 狀態：processing / shipped。
// pending / allocated 等還沒走完取號流程、沒標籤可印；canceled / terminated 不適用。
// OrderShow.tsx 的「重印標籤」按鈕顯示條件也用這個 set 判斷。
export const REPRINTABLE_FULFILLMENT_STATUSES: string[] = ['processing', 'shipped']

interface Fulfillment {
  id: string
  name?: string
  number?: string
  status?: string
  [key: string]: unknown
}

interface ReprintLabelParcel {
  parcel_items?: Array<{ quantity?: number }>
}

interface ReprintLabelOrder {
  fulfillments?: Fulfillment[]
  parcels?: ReprintLabelParcel[]
  [key: string]: unknown
}

interface ReprintLabelModalProps {
  visible: boolean
  onCancel: () => void
  onConfirm: (fulfillmentId: string, quantity: number) => void
  order?: ReprintLabelOrder
  loading?: boolean
}

const ReprintLabelModal: React.FC<ReprintLabelModalProps> = ({
  visible,
  onCancel,
  onConfirm,
  order,
  loading = false,
}) => {
  const { t } = useTranslation()
  const [form] = Form.useForm<{ fulfillment_id: string; quantity: number }>()

  const fulfillments = (order?.fulfillments || []).filter(
    (f) => f.status && REPRINTABLE_FULFILLMENT_STATUSES.includes(f.status)
  )

  // 排除空箱（沒有任何品項數量的箱子，例如最後一個尚未裝箱的空箱）後的實際包裹數。
  // 空箱不需要標籤，故不計入列印份數預設。
  const nonEmptyParcelsCount = (order?.parcels || []).filter((p) =>
    (p?.parcel_items || []).reduce((sum, it) => sum + (Number(it?.quantity) || 0), 0) > 0
  ).length

  // 根據非空包裹數量設置預設值（至少為 1）
  const defaultQuantity = Math.max(1, nonEmptyParcelsCount)

  // 設置預設值：第一個可重印的 fulfillment
  const defaultFulfillmentId = fulfillments.length > 0 ? fulfillments[0].id : null

  // `Form.useForm()` 的 instance 跟 React component 一起留在父層、`destroyOnHidden` 只 destroy 子 DOM
  // 不會清 form fields。沒這段 reset 的話，QC 連續處理多張 FL 時 form 會殘留上一張的 fulfillment_id，
  // dropdown 顯示成上一張的 UUID（雖然 currentQcFulfillment 已換新）。
  //
  // 注意：deps 只認 `visible` — 只在 false→true transition 時 reset。若把 defaultFulfillmentId / defaultQuantity
  // 放進 deps，modal 開啟期間 parent 重算這些值（例如 order.parcels.length 變動）會再次 setFieldsValue
  // 蓋掉使用者已輸入的選擇 — silent UX bug。讀「最新」defaults 改用 ref 規避 stale-closure 問題。
  const prevVisibleRef = useRef(false)
  const defaultsRef = useRef({ fulfillment_id: defaultFulfillmentId, quantity: defaultQuantity })
  defaultsRef.current = { fulfillment_id: defaultFulfillmentId, quantity: defaultQuantity }

  useEffect(() => {
    if (visible && !prevVisibleRef.current) {
      const { fulfillment_id, quantity } = defaultsRef.current
      form.setFieldsValue({ fulfillment_id: fulfillment_id ?? undefined, quantity })
    }
    prevVisibleRef.current = visible
  }, [visible, form])

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      onConfirm(values.fulfillment_id, values.quantity);
    } catch (errorInfo) {
      console.error('Validation failed:', errorInfo);
    }
  };

  const handleCancel = () => {
    form.resetFields()
    onCancel()
  }

  return (
    <Modal
      title={t('shared.orders.detail.actions.reprint_label_title')}
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      okText={t('common.button.confirm')}
      cancelText={t('common.button.cancel')}
      confirmLoading={loading}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ quantity: defaultQuantity, fulfillment_id: defaultFulfillmentId }}
      >
        {/* Fulfillment 選擇 */}
        <Form.Item
          label={t('shared.orders.detail.actions.reprint_label_fulfillment')}
          name="fulfillment_id"
          rules={[
            {
              required: true,
              message: t('shared.orders.detail.actions.reprint_label_fulfillment_required'),
            },
          ]}
        >
          <Select
            placeholder={t('shared.orders.detail.actions.reprint_label_fulfillment_placeholder')}
            disabled={fulfillments.length <= 1}
          >
            {fulfillments.map((fulfillment) => (
              <Select.Option key={fulfillment.id} value={fulfillment.id}>
                <div>
                  <span style={{ fontWeight: 500 }}>
                    {fulfillment.name || fulfillment.number || `#${fulfillment.id}`}
                  </span>
                  <span style={{ marginLeft: 8, color: '#999', fontSize: '12px' }}>
                    ({t(`enums.fulfillment.status.${fulfillment.status}`, { defaultValue: fulfillment.status })})
                  </span>
                </div>
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label={t('shared.orders.detail.actions.reprint_label_quantity')}
          name="quantity"
          rules={[
            {
              required: true,
              message: t('shared.orders.detail.actions.reprint_label_quantity_required'),
            },
            {
              type: 'number',
              min: 1,
              message: t('shared.orders.detail.actions.reprint_label_quantity_min'),
            },
          ]}
        >
          <InputNumber
            min={1}
            max={100}
            style={{ width: '100%' }}
            placeholder={t('shared.orders.detail.actions.reprint_label_quantity_placeholder')}
          />
        </Form.Item>
        <div style={{ color: '#666', fontSize: '12px', marginTop: '-16px' }}>
          {t('shared.orders.detail.actions.reprint_label_hint', {
            parcelsCount: nonEmptyParcelsCount,
          })}
        </div>
      </Form>
    </Modal>
  )
}

export default ReprintLabelModal
